import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import Menu from 'lucide-react/dist/esm/icons/menu';
import X from 'lucide-react/dist/esm/icons/x';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const services = [
    { name: 'Audit & Assurance', path: '/services#external-audit' },
    { name: 'Internal Audit', path: '/services#internal-audit' },
    { name: 'Forensic Investigations', path: '/services#forensic' },
    { name: 'IT Risk & Advisory', path: '/services#it-risk' },
    { name: 'Consulting Services', path: '/services#consulting' },
    { name: 'Public Sector Solutions', path: '/services#public-sector' },
    { name: 'Venture Capital', path: '/services#venture-capital' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
  setIsServicesOpen(false);
}, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  const handleServicesMouseEnter = () => {
    setIsServicesOpen(true);
  };

  const handleServicesMouseLeave = () => {
    setIsServicesOpen(false);
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container-custom">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="https://media.phst.co.za/file/omc/omc-logo.jpg"
              alt="OMC Chartered Accountants"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`font-medium transition-colors bg-transparent focus:outline-none ${
                isActive('/') ? 'text-[#0383ED]' : 'text-gray-700 hover:text-[#0383ED]'
              }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`font-medium transition-colors bg-transparent focus:outline-none ${
                isActive('/about') ? 'text-[#0383ED]' : 'text-gray-700 hover:text-[#0383ED]'
              }`}
            >
              About Us
            </Link>

            {/* Services Dropdown - Now with hover */}
            <div 
              className="relative" 
  ref={dropdownRef}
  onMouseEnter={() => setIsServicesOpen(true)}
  onMouseLeave={() => setIsServicesOpen(false)}
>
  <Link
    to="/services"
    className={`flex items-center font-medium transition-colors bg-transparent focus:outline-none ${
      isActive('/services') ? 'text-[#0383ED]' : 'text-gray-700 hover:text-[#0383ED]'
    }`}
  >
    Services
    <ChevronDown
      className={`ml-1 h-4 w-4 transition-transform ${
        isServicesOpen ? 'rotate-180' : ''
      }`}
    />
              </Link>
              <AnimatePresence>
  {isServicesOpen && (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className="absolute top-full left-0 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50"
      style={{ marginTop: '0px' }} // Remove the gap
    >
      {services.map((service, index) => (
        <Link
  key={index}
  to={service.path}
  onClick={() => setIsServicesOpen(false)}
  className="group relative block px-4 py-3 text-gray-700 text-[17px] rounded-md transition-all duration-300"
>
  <span className="relative z-10 group-hover:text-[#0283ED]">
    {service.name}
  </span>

  {/* Hover gradient overlay */}
  <span
    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-md"
    style={{
      background: 'linear-gradient(to right, rgba(2,131,237,0.18), rgba(2,131,237,0) 90%)',
    }}
  ></span>

  {/* Left border on hover */}
  <span
    className="absolute left-0 top-0 h-full w-[4px] bg-[#0283ED] opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-l-md"
  ></span>
</Link>
      ))}
    </motion.div>
  )}
</AnimatePresence>
            </div>

            <Link
              to="/careers"
              className={`font-medium transition-colors bg-transparent focus:outline-none ${
                isActive('/careers') ? 'text-[#0383ED]' : 'text-gray-700 hover:text-[#0383ED]'
              }`}
            >
              Careers
            </Link>
            <Link
              to="/contact"
              className={`font-medium transition-colors bg-transparent focus:outline-none ${
                isActive('/contact') ? 'text-[#0383ED]' : 'text-gray-700 hover:text-[#0383ED]'
              }`}
            >
              Contact Us
            </Link>
            <Link
              to="/contact#contact-form"
              className="bg-[#0383ED] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#1A95FF] transition-colors"
            >
              Schedule Consultation
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-primary"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden border-t border-gray-200 py-4 overflow-hidden"
            >
              <div className="space-y-4">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-gray-700 hover:text-primary font-medium"
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-gray-700 hover:text-primary font-medium"
                >
                  About Us
                </Link>
                <div>
                  <Link
    to="/services"
    onClick={() => setIsMobileMenuOpen(false)}
    className="block text-gray-700 hover:text-primary font-medium mb-2"
  >
    Services
  </Link>
  <div className="pl-4 space-y-2">
    {services.map((service, index) => (
      <Link
        key={index}
        to={service.path}
        onClick={() => setIsMobileMenuOpen(false)}
        className="block text-gray-600 hover:text-primary"
      >
        {service.name}
      </Link>
    ))}
                  </div>
                </div>
                <Link
                  to="/careers"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-gray-700 hover:text-primary font-medium"
                >
                  Careers
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-gray-700 hover:text-primary font-medium"
                >
                  Contact Us
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-block bg-[#0383ED] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#1A95FF] transition-colors"
                >
                  Schedule Consultation
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;